import React, { useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { deleteUser, onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";

export default function Profile() {
  const [user, setUser] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [emailSubs, setEmailSubs] = useState([]);
  const [dealAlerts, setDealAlerts] = useState([]);
  const [newAlert, setNewAlert] = useState("");
  const [saveStatus, setSaveStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [unsubscribeAll, setUnsubscribeAll] = useState(false);

  const navigate = useNavigate();
  const allSubscriptionOptions = ["Daily Top 10 Deals", "Weekly Top 10 Deals"];

  // Load user info & preferences
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setEmail(currentUser.email);

        try {
          const snap = await getDoc(doc(db, "users", currentUser.uid));
          if (snap.exists()) {
            const data = snap.data();
            setFirstName(data.firstName || "");
            const prefSubs = data.preferences?.emailSubscription;
            if (Array.isArray(prefSubs)) setEmailSubs(prefSubs);
            else if (typeof prefSubs === "string") setEmailSubs([prefSubs]);
            else setEmailSubs([]);
            setDealAlerts(data.preferences?.dealAlerts || []);
            setUnsubscribeAll(data.preferences?.unsubscribeAll || false);
          }
        } catch (err) {
          console.error("Error loading user data:", err);
        }
      } else {
        navigate("/login");
      }
      setLoading(false);
    });

    return () => unsub();
  }, [navigate]);

  // Save preferences
  const handleSavePreferences = async () => {
    if (!user) return;

    try {
      const updatedPrefs = {
        emailSubscription: unsubscribeAll ? [] : emailSubs,
        dealAlerts: unsubscribeAll ? [] : dealAlerts,
        unsubscribeAll,
      };

      await updateDoc(doc(db, "users", user.uid), {
        preferences: updatedPrefs,
      });

      setEmailSubs(updatedPrefs.emailSubscription);
      setDealAlerts(updatedPrefs.dealAlerts);

      setSaveStatus("✅ Preferences saved successfully!");
      setTimeout(() => setSaveStatus(""), 3000);
    } catch (err) {
      console.error("Error saving preferences:", err);
      setSaveStatus("❌ Failed to save preferences.");
    }
  };

  const handleSubscriptionChange = (option) => {
    if (unsubscribeAll) return;
    setEmailSubs((prev) =>
      prev.includes(option)
        ? prev.filter((item) => item !== option)
        : [...prev, option]
    );
  };

  const handleAddAlert = () => {
    if (unsubscribeAll) return;
    if (newAlert.trim() && !dealAlerts.includes(newAlert.trim())) {
      setDealAlerts([...dealAlerts, newAlert.trim()]);
      setNewAlert("");
    }
  };

  const handleRemoveAlert = (alert) => {
    setDealAlerts(dealAlerts.filter((a) => a !== alert));
  };

  const handleDeleteAccount = async () => {
    const confirmDelete = window.confirm(
      "⚠️ Are you sure you want to delete your account? This action cannot be undone."
    );
    if (!confirmDelete) return;

    try {
      if (user) {
        await deleteDoc(doc(db, "users", user.uid));
        await deleteUser(user);
        navigate("/");
      }
    } catch (err) {
      console.error("Error deleting account:", err);
    }
  };

  if (loading)
    return (
      <div className="text-center py-10 text-gray-600">Loading profile...</div>
    );

  return (
    <div className="min-h-[80vh] flex justify-center items-center bg-gradient-to-br from-indigo-50 to-blue-100 px-4">
      <div className="bg-white shadow-lg rounded-2xl p-8 w-full max-w-lg">
        {/* Profile Header */}
        <div className="flex items-center gap-6 border-b pb-6 mb-6">
          <div className="flex-shrink-0 w-20 h-20 rounded-full bg-indigo-600 text-white flex items-center justify-center text-3xl font-bold shadow-md">
            {firstName
              ? firstName.charAt(0).toUpperCase()
              : email.charAt(0).toUpperCase()}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {firstName || "User"}
            </h1>
            <p className="text-gray-500">{email}</p>
          </div>
        </div>

        {/* Preferences Section */}
        <div className="space-y-8">
          {/* Email Subscription */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">
              Email Subscription
            </h2>
            <p className="text-sm text-gray-500 mb-3">
              Receive regular emails based on your preferences.
            </p>
            <div className="space-y-2">
              {allSubscriptionOptions.map((option) => (
                <label
                  key={option}
                  className="flex items-center space-x-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={emailSubs.includes(option)}
                    onChange={() => handleSubscriptionChange(option)}
                    disabled={unsubscribeAll}
                    className="text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-gray-700 text-sm">{option}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Deal Alerts */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-3">
              Deal Alerts
            </h2>
            <p className="text-sm text-gray-500 mb-3">
              Set alerts for keywords like <b>PS5</b>, <b>Xbox</b>, or{" "}
              <b>Laptop</b>.
            </p>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newAlert}
                onChange={(e) => setNewAlert(e.target.value)}
                placeholder="Type keyword and press Add"
                disabled={unsubscribeAll}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none text-sm disabled:bg-gray-100"
              />
              <button
                type="button"
                onClick={handleAddAlert}
                disabled={unsubscribeAll}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  unsubscribeAll
                    ? "bg-gray-400 text-white cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-700 text-white"
                }`}
              >
                Add
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              {dealAlerts.length > 0 ? (
                dealAlerts.map((alert) => (
                  <div
                    key={alert}
                    className="flex items-center gap-1 bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm"
                  >
                    {alert}
                    <button
                      onClick={() => handleRemoveAlert(alert)}
                      className="text-red-500 ml-1 hover:text-red-700 font-bold"
                    >
                      ✕
                    </button>
                  </div>
                ))
              ) : (
                <p className="text-gray-400 text-sm">No alerts set yet.</p>
              )}
            </div>
          </div>

          {/* Unsubscribe from all */}
          <div className="border-t pt-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              Email Preferences
            </h2>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={unsubscribeAll}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setUnsubscribeAll(checked);
                  if (checked) {
                    setEmailSubs([]);
                    setDealAlerts([]);
                  }
                }}
                className="text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-gray-700 text-sm">
                Unsubscribe from all Email Communications
              </span>
            </label>
          </div>
        </div>

        {/* Save + Delete Actions */}
        <div className="mt-8">
          <div className="h-5 mb-3 text-center">
            {saveStatus && (
              <span className="text-green-600 text-sm transition-opacity duration-500">
                {saveStatus}
              </span>
            )}
          </div>

          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <button
              onClick={handleSavePreferences}
              className="bg-indigo-600 text-white text-sm sm:text-base px-4 py-2 sm:px-6 sm:py-3 rounded-md hover:bg-indigo-700 transition w-full sm:w-auto"
            >
              Save Preferences
            </button>
            <button
              onClick={handleDeleteAccount}
              className="bg-red-500 text-white text-sm sm:text-base px-4 py-2 sm:px-6 sm:py-3 rounded-md hover:bg-red-600 transition w-full sm:w-auto"
            >
              Delete Account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
